/** Export workflows: CSV/JSON data, chart PNG/SVG, annotated images and float planes. */
import { areaStats } from '../core/area';
import { colormapLut } from '../core/colormaps';
import { PRIMARIES, TRANSFER_LABELS } from '../core/color';
import { quantityInfo } from '../core/pipeline';
import { areaRows, contextHeader, metricRows, peakRows, profileCsv, toCsv, type ExportContext } from '../core/report';
import { hasAreaStats, type LineRoi } from '../core/roi';
import { unitScale } from '../core/units';
import { encodePfm, encodeTiff } from '../io/encode';
import { buildProfileSpec } from '../ui/panels/ProfilePanel';
import { renderCanvas, renderSvg } from '../plot/render';
import { buildScene } from '../plot/scene';
import { buildColorRGBA } from '../view/colorImage';
import { drawOverlay } from '../view/overlay';
import { contourData, viewportApi } from '../view/viewState';
import {
  display,
  frames,
  lineResults,
  measurement,
  metricSettings,
  notify,
  pipeline,
  quantity,
  rois,
  sampling,
  selectedLine,
  spatial,
  windowRange,
} from './store';

export const APP_NAME = 'Aperto 1.0 — optical inspection & intensity profiling';

export function download(data: Blob | string, filename: string, type = 'text/plain;charset=utf-8') {
  const blob = typeof data === 'string' ? new Blob([data], { type }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function copyText(text: string, message = 'Copied to clipboard') {
  try {
    await navigator.clipboard.writeText(text);
    notify('success', message);
  } catch {
    notify('error', 'Clipboard write was blocked by the browser');
  }
}

function stamp() {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

function baseName() {
  const n = (frames.A.value ?? frames.B.value)?.meta.name ?? 'aperto';
  return (
    n
      .replace(/\.[^.]+$/, '')
      .replace(/[^\w.-]+/g, '_')
      .slice(0, 60) || 'aperto'
  );
}

/** Pipeline and source description shared by every export. */
export function exportContext(): ExportContext {
  const m = measurement.value;
  const f = m?.ctx.frame ?? frames.A.value;
  const sp = spatial.value;
  const p = pipeline.value;
  const enc = m?.ctx.enc;
  const lines: string[] = [];
  if (enc) {
    lines.push(
      `transfer function: ${TRANSFER_LABELS[enc.transfer]}${enc.transfer === 'gamma' ? ` (γ = ${enc.params.gamma})` : ''} [${enc.transferFromAuto ? 'auto' : 'manual'}]`,
      `primaries: ${enc.primaries === 'custom' ? 'custom' : PRIMARIES[enc.primaries].label} [${enc.primariesFromAuto ? 'auto' : 'manual'}]`,
      `encoding hint: ${f?.meta.encoding.description ?? '—'}`,
    );
  }
  lines.push(
    `dark correction: ${p.darkMode}${p.darkMode === 'constant' ? ` (${p.darkConstant})` : ''}; flat field: ${p.flatEnabled ? 'on' : 'off'}`,
  );
  const cal = m?.ctx.cal;
  lines.push(
    `photometric calibration: ${p.calibration.mode === 'none' ? 'none' : `${p.calibration.mode} → ${cal?.description} [${p.calibration.unit}]`}`,
  );
  if (m && m.mode !== 'A') lines.push(`comparison: ${m.label}`);
  return {
    app: APP_NAME,
    generated: new Date().toISOString(),
    source: f
      ? `${f.meta.name} (${f.meta.format}, ${f.width} × ${f.height}, ${f.isFloat ? 'float' : `${f.meta.bitDepth}-bit`}, ${f.channels === 1 ? 'grey' : 'RGB'})`
      : '—',
    quantity: m?.label ?? quantityInfo(quantity.value).label,
    unit: m?.unit ?? '',
    spatialUnit: sp.unit,
    perPixel: unitScale(sp),
    pipeline: lines,
  };
}

function samplingText() {
  const s = sampling.value;
  return `${s.interp} interpolation; step ${s.step} px; band statistic ${s.stat}; out-of-bounds ${s.oob}`;
}

function geometryText(r: LineRoi) {
  const L = Math.hypot(r.x1 - r.x0, r.y1 - r.y0);
  return `start (${r.x0.toFixed(3)}, ${r.y0.toFixed(3)}) px; end (${r.x1.toFixed(3)}, ${r.y1.toFixed(3)}) px; length ${L.toFixed(3)} px; width ${r.width} px`;
}

export function exportProfileCsv(all = false) {
  const res = lineResults.value;
  const sel = selectedLine.value;
  const list = all ? [...res.values()] : sel && res.get(sel.id) ? [res.get(sel.id)!] : [];
  if (!list.length) return notify('warn', 'No line profile to export');
  const ctx = exportContext();
  for (const r of list) {
    const extra = [{ name: 'derivative_per_px', values: r.derivative }];
    if (r.profileB) extra.push({ name: 'value_image_B', values: r.profileB.v });
    const csv = profileCsv(ctx, { name: r.roi.name, geometry: geometryText(r.roi), sampling: samplingText(), profile: r.profile, extra });
    download(csv, `${baseName()}_${r.roi.name.replace(/\s+/g, '_')}_profile_${stamp()}.csv`, 'text/csv;charset=utf-8');
  }
}

export function metricsCsv(): string | null {
  const res = lineResults.value;
  if (!res.size) return null;
  const ctx = exportContext();
  const k = ctx.perPixel;
  const rows: (string | number | null)[][] = [['line', 'metric', 'value', 'unit']];
  for (const r of res.values()) {
    for (const [label, value, unit] of metricRows(r.metrics, ctx.unit, ctx.spatialUnit, k)) rows.push([r.roi.name, label, value, unit]);
    const pr = peakRows(r.metrics, ctx.spatialUnit, k);
    pr.slice(1).forEach((p) => pr[0].slice(1).forEach((h, j) => rows.push([r.roi.name, `peak ${p[0]} ${h}`, p[j + 1], ''])));
  }
  const ms = metricSettings.value;
  return toCsv(rows, [
    ...contextHeader(ctx),
    `sampling: ${samplingText()}`,
    `baseline: ${ms.baseline}; polarity: ${ms.polarity}; peaks: ${ms.peakMode}; width levels: ${ms.widthLevels.join(', ')}; edge levels: ${ms.edgeLow}/${ms.edgeHigh}`,
  ]);
}

export function exportMetricsCsv() {
  const csv = metricsCsv();
  if (!csv) return notify('warn', 'No line profiles to summarise');
  download(csv, `${baseName()}_metrics_${stamp()}.csv`, 'text/csv;charset=utf-8');
}

export function exportAreaCsv() {
  const m = measurement.value;
  const areas = rois.value.filter(hasAreaStats);
  if (!m || !areas.length) return notify('warn', 'No area or point ROIs to export');
  const ctx = exportContext();
  const rows: (string | number | null)[][] = [['roi', 'kind', 'statistic', 'value', 'unit']];
  for (const r of areas) {
    const st = areaStats(r, m.plane, m.clip, m.ctx);
    for (const [label, value, unit] of areaRows(st, ctx.unit, ctx.spatialUnit, ctx.perPixel))
      rows.push([r.name, r.kind, label, value, unit]);
  }
  download(toCsv(rows, contextHeader(ctx)), `${baseName()}_areas_${stamp()}.csv`, 'text/csv;charset=utf-8');
}

/** Complete machine-readable report. */
export function reportJson(): string {
  const ctx = exportContext();
  const m = measurement.value;
  const k = ctx.perPixel;
  const lines = [...lineResults.value.values()].map((r) => ({
    name: r.roi.name,
    geometry: {
      x0: r.roi.x0,
      y0: r.roi.y0,
      x1: r.roi.x1,
      y1: r.roi.y1,
      width: r.roi.width,
      lengthPx: r.profile.length,
      angleDeg: r.profile.angleDeg,
    },
    metrics: Object.fromEntries(metricRows(r.metrics, ctx.unit, ctx.spatialUnit, k).map(([l, v, u]) => [l, { value: v, unit: u }])),
    peaks: r.metrics.peaks.map((p) => ({
      position: p.x * k,
      value: p.value,
      height: p.height,
      prominence: p.prominence,
      widths: p.widths.map((w) => ({
        level: w.level,
        left: w.left === null ? null : w.left * k,
        right: w.right === null ? null : w.right * k,
        width: w.width === null ? null : w.width * k,
      })),
    })),
    profile: { distance: Array.from(r.profile.t, (t) => t * k), value: Array.from(r.profile.v), sd: Array.from(r.profile.sd) },
  }));
  const areas = m
    ? rois.value.filter(hasAreaStats).map((r) => ({
        name: r.name,
        kind: r.kind,
        stats: Object.fromEntries(
          areaRows(areaStats(r, m.plane, m.clip, m.ctx), ctx.unit, ctx.spatialUnit, k).map(([l, v, u]) => [l, { value: v, unit: u }]),
        ),
      }))
    : [];
  return JSON.stringify(
    {
      app: ctx.app,
      generated: ctx.generated,
      source: ctx.source,
      quantity: ctx.quantity,
      unit: ctx.unit,
      spatial: spatial.value,
      pipeline: ctx.pipeline,
      sampling: sampling.value,
      metricSettings: metricSettings.value,
      lines,
      areas,
    },
    (_k, v) => (typeof v === 'number' && !Number.isFinite(v) ? null : v),
    2,
  );
}

export function exportReportJson() {
  download(reportJson(), `${baseName()}_report_${stamp()}.json`, 'application/json');
}

export async function exportChartPng(scale = 2, width = 1200, height = 600) {
  const spec = buildProfileSpec();
  const c = document.createElement('canvas');
  c.width = width * scale;
  c.height = height * scale;
  renderCanvas(c.getContext('2d')!, buildScene({ ...spec, title: chartTitle() }, width, height), scale);
  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/png'));
  if (blob) download(blob, `${baseName()}_profile_${stamp()}.png`);
}

export function exportChartSvg(width = 1200, height = 600) {
  const spec = buildProfileSpec();
  download(renderSvg(buildScene({ ...spec, title: chartTitle() }, width, height)), `${baseName()}_profile_${stamp()}.svg`, 'image/svg+xml');
}

function chartTitle() {
  const sel = selectedLine.value;
  return `${baseName()} — ${sel?.name ?? 'profile'} — ${measurement.value?.label ?? ''}`;
}

export async function exportViewPng() {
  const blob = await viewportApi.current?.snapshotView();
  if (blob) download(blob, `${baseName()}_view_${stamp()}.png`);
}

/** Full-resolution image with the current display mapping and all annotations. */
export async function exportAnnotatedPng() {
  const m = measurement.value;
  if (!m) return notify('warn', 'No image loaded');
  const f = m.ctx.frame;
  const W = f.width;
  const H = f.height;
  if (W * H > 120e6) return notify('error', 'Image too large for an annotated full-resolution export');
  const d = display.value;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d')!;
  const img = g.createImageData(W, H);
  if (d.mode === 'color') {
    img.data.set(buildColorRGBA(m.ctx, d.exposure));
  } else {
    const [lo, hi] = windowRange.value;
    const lut = colormapLut(d.mode === 'gray' ? 'gray' : d.colormap, d.mode === 'gray' ? 0 : d.bands);
    const p = m.plane.data;
    const la = Math.log(lo);
    const lb = Math.log(hi);
    for (let i = 0; i < p.length; i++) {
      const v = p[i];
      let t = d.log ? (Math.log(Math.max(v, 1e-30)) - la) / (lb - la) : (v - lo) / (hi - lo);
      const o = i * 4;
      if (!Number.isFinite(v)) {
        img.data[o] = 90;
        img.data[o + 1] = 0;
        img.data[o + 2] = 90;
      } else {
        t = Math.min(1, Math.max(0, t));
        const k = Math.round(t * 255) * 3;
        img.data[o] = lut[k];
        img.data[o + 1] = lut[k + 1];
        img.data[o + 2] = lut[k + 2];
      }
      img.data[o + 3] = 255;
    }
  }
  // Draw annotations (on their own layer) at a size proportional to the image.
  const s = Math.max(1, Math.max(W, H) / 1600);
  const cd = contourData.value;
  const [lo, hi] = windowRange.value;
  drawOverlay(g, {
    view: { scale: 1 / s, tx: 0, ty: 0 },
    cssW: W / s,
    cssH: H / s,
    dpr: s,
    imageW: W,
    imageH: H,
    rois: rois.value,
    selectedId: null,
    draft: null,
    polygonDraft: null,
    plane: null,
    showGrid: false,
    showValues: false,
    showLabels: d.showLabels,
    showScaleBar: d.showScaleBar,
    spatial: spatial.value,
    contours: cd ? { levels: cd, color: d.contours.color, labels: d.contours.labels } : null,
    plotMarker: null,
    lineMarks: null,
    legend:
      d.showLegend && d.mode !== 'color' ? { mode: d.mode, colormap: d.colormap, bands: d.bands, lo, hi, log: d.log, label: m.axis } : null,
    live: false,
  });
  // Composite: image first, then the annotation layer.
  const out = document.createElement('canvas');
  out.width = W;
  out.height = H;
  const o = out.getContext('2d')!;
  const base = document.createElement('canvas');
  base.width = W;
  base.height = H;
  base.getContext('2d')!.putImageData(img, 0, 0);
  o.drawImage(base, 0, 0);
  o.drawImage(c, 0, 0);
  const blob = await new Promise<Blob | null>((res) => out.toBlob(res, 'image/png'));
  if (blob) download(blob, `${baseName()}_annotated_${stamp()}.png`);
}

/** The measurement plane as a 32-bit float TIFF (with pipeline description) or PFM. */
export function exportPlane(format: 'tiff' | 'pfm') {
  const m = measurement.value;
  if (!m) return notify('warn', 'No image loaded');
  const ctx = exportContext();
  const sp = spatial.value;
  if (format === 'tiff') {
    const desc = [...contextHeader(ctx)].join('\n');
    const pxPerCm =
      sp.unit === 'mm'
        ? 10 / sp.perPixel
        : sp.unit === 'cm'
          ? 1 / sp.perPixel
          : sp.unit === 'µm'
            ? 1e4 / sp.perPixel
            : sp.unit === 'm'
              ? 0.01 / sp.perPixel
              : undefined;
    const bytes = encodeTiff({
      width: m.plane.width,
      height: m.plane.height,
      channels: 1,
      data: m.plane.data,
      description: desc,
      pixelsPerCm: pxPerCm,
    });
    download(new Blob([bytes as BlobPart], { type: 'image/tiff' }), `${baseName()}_${quantity.value}_${stamp()}.tif`);
  } else {
    const bytes = encodePfm(m.plane.width, m.plane.height, 1, m.plane.data);
    download(new Blob([bytes as BlobPart], { type: 'application/octet-stream' }), `${baseName()}_${quantity.value}_${stamp()}.pfm`);
  }
}
